import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import api from 'utils/api';
import { useSelector } from 'react-redux';
import Avatar from 'components/Avatar';
import Text from 'components/styled/Text';
import { GridList } from '../GridList';
import TextInput from '../TextInput';
import Button from '../Button';
import Modal from './Modal';
import { useReduxActions } from 'hooks/useReduxActions';
import { searchReportJobsSelector } from '../../store/selectors';

const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 582px;
  padding: 16px;
  input {
    width: 433px;
  }
`;

const TableContainer = styled.div`
  height: 256px;
  overflow: auto;
  margin-top: 24px;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: end;
  margin-top: 16px;
  > button {
    width: 140px;
  }
`;

function AddToReportModal({ isOpen, isChosen, onClose }) {
  const gridRef = useRef(null);
  const { t } = useTranslation();
  const actions = useReduxActions();
  const searchReportJobs = useSelector(searchReportJobsSelector);
  const [search, setSearch] = useState('');
  const [columnDefs] = useState([
    {
      maxWidth: 66,
      checkboxSelection: true,
      showDisabledCheckboxes: true,
    },
    {
      field: 'user',
      cellStyle: { display: 'flex', alignItems: 'center' },
      cellRenderer: ({ data }) => (
        <>
          <Avatar size="medium" image={data.user.profile?.avatar?.url} />
          <Text variant="body-small">{`${data.user.profile?.first_name} ${data.user.profile?.last_name}`}</Text>
        </>
      ),
    },
    {
      field: 'ID',
    },
  ]);

  const handleChangeSearch = event => {
    setSearch(new RegExp(event.target.value, 'ig'));
  };

  const handleAddProductToReport = async () => {
    const chosenProducts = isChosen.current.api.getSelectedRows();
    const chosenSearchReport = gridRef.current.api.getSelectedRows();
    const ids = chosenSearchReport.map(item => item.ID);
    const dataObj = {
      ids,
      data: chosenProducts,
    };
    if (chosenSearchReport) {
      const response = await api.patch(`job/add-product`, dataObj);
      if (response) {
        onClose();
      }
    }
  };

  useEffect(() => {
    actions.getSearchReportJobs();
  }, []); // eslint-disable-line

  const filteredSearchReportJobs = searchReportJobs.filter(
    job =>
      job.ID.match(search) ||
      job.user.profile?.first_name?.match(search) ||
      job.user.profile?.last_name?.match(search) ||
      job.user.client_id?.match(search),
  );
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('add.to.report.modal.title')}>
      <ModalContainer>
        <TextInput
          type="text"
          placeholder={t('add.to.report.modal.input.placeholder')}
          onChange={handleChangeSearch}
        />
        <TableContainer>
          <GridList
            hideHeader
            rows={filteredSearchReportJobs}
            columnDefs={columnDefs}
            ref={gridRef}
            rowSelection="multiple"
          />
        </TableContainer>
        <ButtonContainer>
          <Button
            size="large"
            variant="primary"
            onClick={handleAddProductToReport}>
            {t('add.to.report.modal.button')}
          </Button>
        </ButtonContainer>
      </ModalContainer>
    </Modal>
  );
}

AddToReportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isChosen: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AddToReportModal;
